/**
 * ViewSelector Component
 *
 * Displays available views from the active persona/tool as a radio button group.
 * Replaces the old 2D/3D toggle with a dynamic view selection based on
 * the persona system's view configuration.
 *
 * View state is consumed from the useViewManager hook, which subscribes directly to potree's ViewManager.
 */

import { memo, useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useViewManager } from '../../hooks/useViewManager';
import { useRenderViewState } from '../../hooks/useRenderViewState';
import { RenderViewContext } from '../../contexts/RenderViewContext';
import { RendererContext } from '../../contexts/RendererContext';
import { T } from '../../translation/src';

interface ViewSelectorProps {
  className?: string;
}

interface ViewButtonProps {
  /** Either a pre-localized string (global views) or a JSX node (custom views via <T>). */
  label: React.ReactNode;
  active: boolean;
  onClick: () => void;
  className?: string;
}

export const ViewButton: React.FC<ViewButtonProps> = ({ label, active, onClick, className }) => {
  return (
    <div
      onClick={onClick}
      className={classNames(
        'flex items-center justify-center cursor-pointer px-3 h-9',
        !active && 'bg-[rgb(230,230,230)] dark:bg-[rgb(33,33,33)] dark:text-white hover:bg-neon-green-300 dark:hover:text-neon-green-300 dark:hover:bg-[#868994]',
        active && 'bg-neon-green-300 text-black',
        className,
      )}
    >
      <div className="whitespace-nowrap">{label}</div>
    </div>
  );
};

interface UnifiedTab {
  id: string;
  label: React.ReactNode;
}

const ViewSelector_: React.FC<ViewSelectorProps> = ({ className }) => {
  // Per-RenderView state (widget) when wrapped in RenderViewContext;
  // otherwise the global ViewManager (main view's switcher behavior).
  const renderView = useContext(RenderViewContext);
  const widgetState = useRenderViewState(renderView?.viewState ?? null);
  const global = useViewManager();
  const { availableViews, setView } = renderView ? widgetState : global;

  // Custom views (tool-spawned tabs like wall-focus) come from the active
  // RenderViewState. For widgets that's `renderView.viewState`; for the
  // main path it's `viewer.renderViews[0].viewState` (the main view also
  // has a state now — see viewer.ts `ensureMainView`). The state's
  // `customViews` ReactiveProperty is what populates the card UI.
  const { viewer } = useContext(RendererContext);
  const mainRenderViewState = !renderView && viewer ? viewer.renderViews[0]?.viewState ?? null : null;
  const mainState = useRenderViewState(mainRenderViewState);
  const customViews = useMemo(
    () => (renderView ? widgetState.customViews : mainState.customViews),
    [renderView, widgetState.customViews, mainState.customViews],
  );
  // Active-id resolution: widgets read straight from their state; main reads
  // global by default but yields to the main state when a custom view is
  // active (the global ViewManager never sees `custom:` ids).
  const currentViewId = renderView
    ? widgetState.currentViewId
    : mainState.currentViewId && mainState.currentViewId.startsWith('custom:')
      ? mainState.currentViewId
      : global.currentViewId;

  // Merge global + custom into a single tab list. Globals first, customs
  // appended on the right — matches the user's mental model that customs
  // are tool-spawned tabs that join the existing row.
  const tabs = useMemo<UnifiedTab[]>(() => {
    const out: UnifiedTab[] = availableViews.map((v) => ({ id: v.id, label: v.name }));
    for (const v of customViews) {
      // Custom-view labels are raw English strings supplied by the tool;
      // route them through <T> so the translation pipeline can resolve.
      out.push({ id: v.id, label: <T _str={v.label} swc /> });
    }
    return out;
  }, [availableViews, customViews]);

  const handleViewClick = useCallback(
    (viewId: string) => {
      if (currentViewId === viewId) return;
      // On the main path (no enclosing RenderViewContext), `mainState`
      // owns custom-view tabs that the global ViewManager doesn't know
      // about. Two cases route through `mainState.setView` instead of
      // the global setter:
      //
      //   1. Switching INTO a custom view (id starts with `custom:`).
      //      Global manager can't apply it; only RenderViewState can.
      //   2. Switching FROM a custom view to a built-in one. The user
      //      clicking the X-Ray / 3D tab while wall focus is active must
      //      atomically tear the custom view down — `mainState.setView`
      //      runs `applyOverridesFromOrigin` then `applyView` in a single
      //      transaction (clip, material, camera-clip restore + global
      //      view's declarative attrs). Going through `persona.setView`
      //      (global) here would leave the custom view's overrides
      //      orphaned on the main RenderView's render path.
      //
      // Widgets always use their own state, untouched (preserves the
      // RenderViewContext branch's prior behavior).
      const onMain = !renderView && mainRenderViewState;
      const goingToCustom = viewId.startsWith('custom:');
      const leavingCustom = !!mainState.currentViewId?.startsWith('custom:');
      if (onMain && (goingToCustom || leavingCustom)) {
        mainState.setView(viewId);
        return;
      }
      setView(viewId);
    },
    [currentViewId, setView, renderView, mainRenderViewState, mainState],
  );

  if (tabs.length === 0) {
    return null;
  }

  return (
    <div className={classNames('flex gap-px z-10 w-auto', className)}>
      {tabs.map((tab, index) => {
        const isFirst = index === 0;
        const isLast = index === tabs.length - 1;
        const isActive = currentViewId === tab.id;

        return (
          <ViewButton
            key={tab.id}
            label={tab.label}
            active={isActive}
            onClick={() => handleViewClick(tab.id)}
            className={classNames({
              'rounded-l-lg': isFirst,
              'rounded-r-lg': isLast,
            })}
          />
        );
      })}
    </div>
  );
};

export const ViewSelector = memo(ViewSelector_);
